import { getAccessToken, getSafeReturnTo, storePendingReturnTo, userManager } from './oidc'

type FetchDependencies = {
  getToken: () => Promise<string | null>
  fetcher?: typeof fetch
  onUnauthorized?: () => Promise<void>
}

export const createAuthorizedFetch = ({
  getToken,
  fetcher = fetch,
  onUnauthorized,
}: FetchDependencies): typeof fetch => {
  let unauthorizedInFlight: Promise<void> | null = null
  return async (input, init = {}) => {
    const token = await getToken()
    const headers = new Headers(init.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const response = await fetcher(input, { ...init, headers: Object.fromEntries(headers) })
    if (response.status === 401 && onUnauthorized && !unauthorizedInFlight) {
      unauthorizedInFlight = onUnauthorized()
    }
    return response
  }
}

const REAUTH_KEY = 'lightscape.auth.lastReauth'
const REAUTH_WINDOW_MS = 60_000

/**
 * A fresh sign-in that the API still rejects means the API and identity provider disagree
 * (issuer, audience, clock) — redirecting again would loop forever. Stop and say so instead.
 */
const reauthLooping = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(REAUTH_KEY) ?? 0)
    if (Date.now() - last < REAUTH_WINDOW_MS) return true
    sessionStorage.setItem(REAUTH_KEY, String(Date.now()))
  } catch {
    /* storage unavailable: allow the redirect */
  }
  return false
}

const handleUnauthorized = async () => {
  if (reauthLooping()) {
    console.error('[auth] the API rejected a fresh sign-in; not redirecting again')
    return
  }
  await userManager?.removeUser().catch(() => undefined)
  const { pathname, search, hash } = window.location
  const returnTo = getSafeReturnTo(`${pathname}${search}${hash}`)
  storePendingReturnTo(returnTo)
  window.location.assign('/auth')
}

export const authorizedFetch = createAuthorizedFetch({
  getToken: getAccessToken,
  onUnauthorized: handleUnauthorized,
})
